// Templates de mensagem (WhatsApp e e-mail) — specs/15-email.md §2.
import { CATEGORIAS_TEMPLATE, ehHtmlEmail, htmlParaTexto, LIMITE_CORPO, montarContexto, renderizar, renderizarEmail, VARIAVEIS, type Canal } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, apiBlob, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Toast, useToast } from '../components/ui';
import { hojeISO } from '../format';
import { prepararImagem } from '../imagem';
import { corpoEmailFinal, corpoEmailVazio, EditorEmail, paraHtmlEmail, PreviaAssinatura, PreviaEmailHtml } from './EditorEmail';
import type { MinhaCaixa } from './Configuracoes';
import { botaoWhatsappInicial, botaoWhatsappPayload, CamposBotaoWhatsapp, textoBotaoExemplo, type BotaoWhatsappValor } from './BotaoWhatsapp';

export interface Template {
  id: string; nome: string; canal: Canal; categoria: string | null; assunto: string | null; corpo: string;
  criadoPor: string; criadoPorNome: string; temImagem: boolean; usos: number; updatedAt: string;
  whatsappBotao?: boolean; whatsappTexto?: string | null; whatsappMensagem?: string | null; whatsappStatusId?: string | null; whatsappProtegeAtivo?: boolean;
}

export const useTemplates = (canal?: Canal) =>
  useQuery({ queryKey: ['templates', canal ?? 'todos'], queryFn: () => api<Template[]>(`/templates${canal ? `?canal=${canal}` : ''}`), staleTime: 30_000 });

const CLIENTE_EXEMPLO = { nome: 'Mariana Souza', empresa: 'Drogaria Vida Nova', cidade: 'Londrina', ultimaCompra: '2026-08-28', valorUltimaCompra: 3480 };
const CANAL_LABEL: Record<Canal, string> = { WHATSAPP: 'WhatsApp', EMAIL: 'E-mail' };

interface Rascunho { nome: string; canal: Canal; categoria: string; assunto: string; corpo: string }
const NOVO: Rascunho = { nome: '', canal: 'WHATSAPP', categoria: '', assunto: '', corpo: 'Olá {{primeiro_nome}}, tudo bem? Aqui é {{vendedor}}, da {{empresa_vendedora}}.' };

export function TemplatesPage() {
  const { me, pode } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const [filtro, setFiltro] = useState<'TODOS' | Canal>('TODOS');
  const lista = useTemplates();
  // id selecionado; 'novo' = criando (copia = duplicar)
  const [sel, setSel] = useState<string | null>(null);
  const [copia, setCopia] = useState<Rascunho | null>(null);

  const itens = (lista.data ?? []).filter((t) => filtro === 'TODOS' || t.canal === filtro);
  useEffect(() => {
    if (sel === null && lista.data?.length) setSel(lista.data[0]!.id);
  }, [lista.data, sel]);
  const atual = sel && sel !== 'novo' ? lista.data?.find((t) => t.id === sel) : undefined;

  const podeEditar = (t?: Template) => pode('CAMPANHAS_CRIAR') && (!t || t.criadoPor === me?.usuario.id || pode('CAMPANHAS_GERENCIAR'));

  return (
    <>
      <div className="page-head">
        <div className="col" style={{ gap: 4 }}>
          <h1>Templates</h1>
          <span className="muted">Mensagens prontas para WhatsApp e e-mail, com variáveis preenchidas pelo cliente.</span>
        </div>
        {pode('CAMPANHAS_CRIAR') && (
          <div className="actions">
            <button className="btn primary" onClick={() => { setCopia(null); setSel('novo'); }}><Icon name="mais" size={15} />Novo template</button>
          </div>
        )}
      </div>

      {lista.isLoading ? <Carregando /> : lista.error ? <Erro erro={mensagemErro(lista.error)} /> : (
        <div className="row" style={{ gap: 20 }}>
          <section className="card" style={{ flex: '1 1 300px', gap: 10 }} aria-label="Lista de templates">
            <div className="seg" role="group" aria-label="Filtrar por canal">
              {(['TODOS', 'WHATSAPP', 'EMAIL'] as const).map((f) => (
                <button key={f} type="button" aria-pressed={filtro === f} onClick={() => setFiltro(f)}>{f === 'TODOS' ? 'Todos' : CANAL_LABEL[f]}</button>
              ))}
            </div>
            {!itens.length && <div className="empty" style={{ padding: 20 }}>Nenhum template{filtro !== 'TODOS' ? ` de ${CANAL_LABEL[filtro]}` : ''} ainda.</div>}
            <div className="tpl-lista">
              {itens.map((t) => (
                <button key={t.id} type="button" className="tpl-item" aria-current={sel === t.id} onClick={() => setSel(t.id)}>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <strong style={{ fontSize: 13.5, fontWeight: 600 }}>{t.nome}</strong>
                    <span className={`badge ${t.canal === 'EMAIL' ? 'blue' : 'green'}`}>{CANAL_LABEL[t.canal]}</span>
                  </span>
                  <span className="small faint">
                    {[t.categoria, `usado ${t.usos}×`, t.temImagem ? 'com imagem' : null, t.criadoPorNome].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {(sel === 'novo' || atual) && (
            <Editor
              key={sel === 'novo' ? `novo-${copia?.nome ?? ''}` : atual!.id}
              template={sel === 'novo' ? undefined : atual}
              inicial={sel === 'novo' ? copia ?? NOVO : undefined}
              editavel={podeEditar(sel === 'novo' ? undefined : atual)}
              onSalvo={async (t, msg) => {
                await qc.invalidateQueries({ queryKey: ['templates'] });
                setSel(t.id);
                toast.show(msg);
              }}
              onExcluido={async () => {
                await qc.invalidateQueries({ queryKey: ['templates'] });
                setSel(null);
                toast.show('Template excluído.');
              }}
              onDuplicar={(r) => { setCopia({ ...r, nome: `${r.nome} (cópia)`.slice(0, 80) }); setSel('novo'); }}
            />
          )}
        </div>
      )}
      <Toast msg={toast.msg} onDone={toast.clear} />
    </>
  );
}

function Editor({ template, inicial, editavel, onSalvo, onExcluido, onDuplicar }: {
  template?: Template; inicial?: Rascunho; editavel: boolean;
  onSalvo: (t: Template, msg: string) => void; onExcluido: () => void; onDuplicar: (r: Rascunho) => void;
}) {
  const { me } = useAuth();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [r, setR] = useState<Rascunho>(() => template
    ? { nome: template.nome, canal: template.canal, categoria: template.categoria ?? '', assunto: template.assunto ?? '', corpo: template.corpo }
    : inicial ?? NOVO);
  // botão "Falar no WhatsApp" (só template de e-mail, specs/24)
  const [wa, setWa] = useState<BotaoWhatsappValor>(() => botaoWhatsappInicial(template));
  // undefined = sem mudança; null = remover; string = nova imagem (data URL)
  const [novaImagem, setNovaImagem] = useState<string | null | undefined>(undefined);
  const [imagemAtual, setImagemAtual] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const caixa = useQuery({ queryKey: ['minha-caixa'], queryFn: () => api<MinhaCaixa>('/email/minha-caixa'), staleTime: 30_000 });

  useEffect(() => {
    if (!template?.temImagem) return;
    let url: string | null = null;
    apiBlob(`/templates/${template.id}/imagem`).then((b) => setImagemAtual((url = URL.createObjectURL(b)))).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [template?.id, template?.temImagem]);

  const email = r.canal === 'EMAIL';
  const imagem = novaImagem === undefined ? imagemAtual : novaImagem;
  const set = <K extends keyof Rascunho>(k: K, v: Rascunho[K]) => setR((x) => ({ ...x, [k]: v }));
  const ctx = montarContexto({ cliente: CLIENTE_EXEMPLO, vendedorNome: me?.usuario.nome, empresaVendedora: me?.empresa.nome, hoje: hojeISO() });
  // e-mail: corpo em HTML do editor (specs/25); WhatsApp: texto
  const corpoFinal = email ? corpoEmailFinal(r.corpo) : r.corpo;
  const previaCorpo = email ? renderizarEmail(corpoFinal, ctx) : renderizar(r.corpo, ctx);
  const corpoVazio = email ? corpoEmailVazio(corpoFinal) : !r.corpo.trim();
  const trocarCanal = (c: Canal) => setR((x) => ({
    ...x, canal: c,
    corpo: c === 'EMAIL' ? paraHtmlEmail(x.corpo) : ehHtmlEmail(x.corpo) ? htmlParaTexto(x.corpo) : x.corpo,
  }));
  const previaAssunto = renderizar(r.assunto, ctx);
  const faltando = [...new Set([...previaCorpo.faltando, ...(email ? previaAssunto.faltando : [])])];
  const limite = LIMITE_CORPO[r.canal];

  const inserir = (v: string) => {
    const el = ref.current;
    const token = `{{${v}}}`;
    if (!el) return set('corpo', r.corpo + token);
    const ini = el.selectionStart ?? r.corpo.length;
    const fim = el.selectionEnd ?? r.corpo.length;
    set('corpo', r.corpo.slice(0, ini) + token + r.corpo.slice(fim));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ini + token.length, ini + token.length); });
  };

  async function escolherImagem(arquivo: File | undefined) {
    if (!arquivo) return;
    setErro(null);
    try { setNovaImagem(await prepararImagem(arquivo)); } catch (e) { setErro(e instanceof Error ? e.message : String(e)); }
  }

  async function salvar() {
    setErro(null);
    setSalvando(true);
    const body = { nome: r.nome, canal: r.canal, categoria: r.categoria || null, assunto: email ? r.assunto : null, corpo: corpoFinal, ...botaoWhatsappPayload(wa, email) };
    try {
      const t = template
        ? await api<Template>(`/templates/${template.id}`, { method: 'PATCH', body })
        : await api<Template>('/templates', { body });
      if (!email && typeof novaImagem === 'string') await api(`/templates/${t.id}/imagem`, { method: 'PUT', body: { dataUrl: novaImagem } });
      else if (!email && novaImagem === null && template?.temImagem) await api(`/templates/${t.id}/imagem`, { method: 'DELETE' });
      onSalvo(t, template ? 'Template salvo.' : 'Template criado.');
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  async function excluir() {
    try { await api(`/templates/${template!.id}`, { method: 'DELETE' }); onExcluido(); } catch (e) { setErro(mensagemErro(e)); }
  }

  return (
    <section className="card" style={{ flex: '2 1 520px' }} aria-label={template ? `Editar ${template.nome}` : 'Novo template'}>
      <Erro erro={erro} />
      {!editavel && <div className="alert info"><Icon name="alerta" /><span>Template de {template?.criadoPorNome}. Só quem criou ou um administrador pode alterar — use “Duplicar” para ter uma versão sua.</span></div>}
      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <label className="field"><span>Nome *</span><input className="input" value={r.nome} maxLength={80} disabled={!editavel} onChange={(e) => set('nome', e.target.value)} placeholder="Ex.: Envio de tabela" /></label>
        <label className="field"><span>Canal</span>
          <select className="select" value={r.canal} disabled={!editavel} onChange={(e) => trocarCanal(e.target.value as Canal)}>
            <option value="WHATSAPP">WhatsApp</option><option value="EMAIL">E-mail</option>
          </select>
        </label>
        <label className="field"><span>Categoria</span>
          <select className="select" value={r.categoria} disabled={!editavel} onChange={(e) => set('categoria', e.target.value)}>
            <option value="">—</option>
            {CATEGORIAS_TEMPLATE.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
      </div>

      {email && <label className="field"><span>Assunto *</span><input className="input" value={r.assunto} maxLength={200} disabled={!editavel} onChange={(e) => set('assunto', e.target.value)} /></label>}

      {!email && (
        <div className="field">
          <span>Imagem (opcional)</span>
          <div className="img-drop">
            {imagem ? <img src={imagem} alt="Imagem do template" /> : <div className="vazio"><Icon name="imagem" size={22} /></div>}
            <div className="col" style={{ gap: 2, flex: '1 1 180px' }}>
              <strong style={{ fontSize: 13.5, fontWeight: 500 }}>{imagem ? 'Imagem enviada junto com a mensagem' : 'Nenhuma imagem'}</strong>
              <span className="small faint">JPG, PNG ou WebP. Ao usar o template numa campanha, a imagem vai junto e é copiada na hora de abrir o WhatsApp (Ctrl+V na conversa).</span>
            </div>
            {editavel && (
              <div style={{ display: 'flex', gap: 6 }}>
                <label className="btn sm">
                  {imagem ? 'Trocar' : 'Escolher imagem'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void escolherImagem(e.target.files?.[0]); e.target.value = ''; }} />
                </label>
                {imagem && <button type="button" className="btn sm" onClick={() => setNovaImagem(null)}>Remover</button>}
              </div>
            )}
          </div>
          {template?.temImagem && email && <span className="hint warn">Ao salvar como e-mail, a imagem do WhatsApp é removida.</span>}
        </div>
      )}

      <label className="field">
        <span style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{email ? 'Corpo do e-mail *' : imagem ? 'Legenda / mensagem *' : 'Mensagem *'}</span>
          <span className={`num ${corpoFinal.length > limite ? '' : 'faint'}`} style={corpoFinal.length > limite ? { color: 'var(--red-text)' } : undefined}>{corpoFinal.length.toLocaleString('pt-BR')} / {limite.toLocaleString('pt-BR')}</span>
        </span>
        {email
          ? <EditorEmail valor={r.corpo} onChange={(v) => set('corpo', v)} disabled={!editavel} minAltura={260} rotulo="Corpo do e-mail" />
          : <textarea ref={ref} className="textarea" rows={6} value={r.corpo} disabled={!editavel} onChange={(e) => set('corpo', e.target.value)} />}
      </label>
      {editavel && email && <span className="small faint">Variáveis no botão “Variável” da barra. Use {'{{variavel|texto padrão}}'} para quando o cliente não tiver o dado.</span>}
      {editavel && !email && (
        <div className="col" style={{ gap: 6 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span className="small faint">Inserir:</span>
            {VARIAVEIS.map((v) => <button type="button" key={v} className="var-btn" onClick={() => inserir(v)}>{`{{${v}}}`}</button>)}
          </div>
          <span className="small faint">Use {'{{variavel|texto padrão}}'} para quando o cliente não tiver o dado. Ex.: {'{{empresa|sua empresa}}'}.</span>
        </div>
      )}

      {email && <CamposBotaoWhatsapp valor={wa} onChange={setWa} ctxExemplo={ctx} disabled={!editavel} ondeStatus="template" />}

      <div className="col" style={{ gap: 8, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <span className="small muted">Pré-visualização com cliente de exemplo · {CLIENTE_EXEMPLO.empresa}</span>
        {email ? (
          <PreviaEmailHtml html={corpoFinal} ctx={ctx} assunto={previaAssunto.texto}>
            {wa.botao && <div><span className="previa-botao-wa">{textoBotaoExemplo(wa, ctx)}</span></div>}
            {caixa.data?.caixa && (caixa.data.caixa.assinatura || caixa.data.caixa.assinaturaImagemUrl)
              ? <PreviaAssinatura texto={caixa.data.caixa.assinatura} imagemUrl={caixa.data.caixa.assinaturaImagemUrl} largura={caixa.data.caixa.assinaturaImagemLargura} posicao={caixa.data.caixa.assinaturaImagemPosicao} />
              : <div className="assinatura">Sua assinatura (Configurações › E-mail de saída)</div>}
          </PreviaEmailHtml>
        ) : (
          <div className="previa-wa">
            <div className="balao">
              {imagem && <img src={imagem} alt="" />}
              <p>{previaCorpo.texto || '—'}</p>
            </div>
          </div>
        )}
        {faltando.length > 0 && <span className="small" style={{ color: 'var(--amber-text)' }}>Sem valor e sem texto padrão: {faltando.join(', ')}</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
        {template && editavel && (confirmarExclusao
          ? <><span className="small muted">Excluir “{template.nome}”?</span><button className="btn danger sm" onClick={excluir}>Excluir</button><button className="btn sm" onClick={() => setConfirmarExclusao(false)}>Cancelar</button></>
          : <button className="btn sm danger" style={{ marginRight: 'auto' }} onClick={() => setConfirmarExclusao(true)}>Excluir</button>)}
        {template && <button className="btn" onClick={() => onDuplicar(r)}>Duplicar</button>}
        {editavel && (
          <button className="btn primary" disabled={salvando || r.nome.trim().length < 2 || corpoVazio || (email && !r.assunto.trim()) || corpoFinal.length > limite} onClick={salvar}>
            {salvando ? 'Salvando…' : template ? 'Salvar template' : 'Criar template'}
          </button>
        )}
      </div>
    </section>
  );
}
